import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ImageOff, Loader2, Search, Trash2, X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Field, Section, Toggle } from '@/components/ui/editor-fields';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { useCatalogSearch } from '@/features/modifiers/modifiers.api';
import { fromPercent, toPercent } from '@/lib/format';
import { formatStoreTime, toApiDate } from '@/lib/store-time';
import { useRemoveReward, useSaveReward } from './loyalty.api';
import {
  REWARD_TYPE,
  isPercentage,
  needsItem,
  type LoyaltyRewardInput,
  type RewardItem,
} from './types';

/**
 * A loyalty reward.
 *
 * Two fields decide what the rest mean. The type picks whether `value` is a
 * currency amount or a fraction, and whether a product has to be attached; the
 * ceiling is applied to the result whatever the type is.
 *
 * That ceiling is the one that bites. `OrdersHelper` ends every redemption with
 * an unconditional `if (Discount > MaxValue) Discount = MaxValue`, so a reward
 * saved with a ceiling of zero discounts nothing — and the original defaulted
 * it to zero and never mentioned it. It is required here.
 */

const dayToApi = (value: string, endOfDay = false) =>
  value ? toApiDate(value, endOfDay) : null;

export function RewardEditor({
  reward,
  /** The attached product, when the row being edited already had one. */
  attachedItem,
  open,
  onOpenChange,
}: {
  reward: LoyaltyRewardInput | null;
  attachedItem?: RewardItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();

  const [draft, setDraft] = useState<LoyaltyRewardInput | null>(reward);
  const [percentText, setPercentText] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [item, setItem] = useState<RewardItem | null>(attachedItem ?? null);
  const [itemQuery, setItemQuery] = useState('');

  const save = useSaveReward();
  const remove = useRemoveReward();
  const catalogSearch = useCatalogSearch();

  useEffect(() => {
    setDraft(reward);
    setPercentText(
      reward && isPercentage(reward.type) ? String(toPercent(reward.value)) : ''
    );
    setStart(reward?.startDate ? formatStoreTime(reward.startDate, true) : '');
    setEnd(reward?.endDate ? formatStoreTime(reward.endDate, true) : '');
    setItem(attachedItem ?? null);
    setItemQuery('');
    catalogSearch.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reward, attachedItem]);

  if (!draft) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-lg" />
      </Sheet>
    );
  }

  const isEdit = !!draft.id;
  const busy = save.isPending || remove.isPending;

  const set = <K extends keyof LoyaltyRewardInput>(
    key: K,
    value: LoyaltyRewardInput[K]
  ) => setDraft((current) => (current ? { ...current, [key]: value } : current));

  /** A currency amount and a fraction are not interchangeable; start over. */
  function setType(next: number) {
    setDraft((current) =>
      current
        ? {
            ...current,
            type: next,
            value: 0,
            itemId: needsItem(next) ? current.itemId : null,
          }
        : current
    );
    setPercentText('');
    if (!needsItem(next)) setItem(null);
  }

  const percent = isPercentage(draft.type);
  const startsAfterEnds = !!start && !!end && Date.parse(start) > Date.parse(end);

  const canSave =
    !!draft.internalName.trim() &&
    !!draft.name.trim() &&
    draft.pointForRedeem > 0 &&
    draft.value > 0 &&
    draft.maxValue > 0 &&
    !!start &&
    !!end &&
    !startsAfterEnds &&
    (!needsItem(draft.type) || !!item);

  const failed = (error: unknown) =>
    toast.error(
      error instanceof Error ? error.message : t('loyalty.saveFailed')
    );

  function submit() {
    save.mutate(
      {
        ...draft!,
        itemId: needsItem(draft!.type) ? (item?.id ?? null) : null,
        startDate: dayToApi(start),
        endDate: dayToApi(end, true),
      },
      {
        onSuccess: () => {
          toast.success(isEdit ? t('loyalty.updated') : t('loyalty.created'));
          onOpenChange(false);
        },
        onError: failed,
      }
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-lg"
      >
        <SheetHeader className="flex-row items-center justify-between gap-3 border-b pr-12">
          <SheetTitle>
            {isEdit ? t('loyalty.editReward') : t('loyalty.newReward')}
          </SheetTitle>
          {isEdit && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive"
                  disabled={busy}
                >
                  <Trash2 className="size-3.5" />
                  {t('loyalty.remove')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('loyalty.remove')}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('loyalty.removeConfirm', { name: draft.internalName })}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={() =>
                      remove.mutate(draft.id!, {
                        onSuccess: () => {
                          toast.success(t('loyalty.removed'));
                          onOpenChange(false);
                        },
                        onError: failed,
                      })
                    }
                  >
                    {t('loyalty.remove')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </SheetHeader>

        <div className="space-y-6 p-4">
          <Section title={t('loyalty.details')}>
            <Field
              label={t('loyalty.internalName')}
              htmlFor="internalName"
              hint={t('loyalty.internalNameHint')}
            >
              <Input
                id="internalName"
                value={draft.internalName}
                disabled={busy}
                onChange={(event) => set('internalName', event.target.value)}
              />
            </Field>
            <Field
              label={t('loyalty.name')}
              htmlFor="name"
              hint={t('loyalty.nameHint')}
            >
              <Input
                id="name"
                value={draft.name}
                disabled={busy}
                onChange={(event) => set('name', event.target.value)}
              />
            </Field>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label={t('loyalty.points')}
                htmlFor="points"
                hint={t('loyalty.pointsHint')}
              >
                <Input
                  id="points"
                  type="number"
                  min={1}
                  value={draft.pointForRedeem}
                  disabled={busy}
                  onChange={(event) =>
                    set('pointForRedeem', Number(event.target.value))
                  }
                />
              </Field>
              <Field
                label={t('loyalty.ordering')}
                htmlFor="ordering"
                hint={t('loyalty.orderingHint')}
              >
                <Input
                  id="ordering"
                  type="number"
                  value={draft.ordering}
                  disabled={busy}
                  onChange={(event) =>
                    set('ordering', Number(event.target.value))
                  }
                />
              </Field>
            </div>

            <Toggle
              label={t('loyalty.isActive')}
              hint={t('loyalty.isActiveHint')}
              checked={draft.isActive}
              disabled={busy}
              onChange={(value) => set('isActive', value)}
            />
          </Section>

          {/* ------------------------------------------------- the reward */}
          <Section title={t('loyalty.whatItGives')}>
            <Field label={t('loyalty.type')} hint={t('loyalty.typeHint')}>
              <Select
                value={String(draft.type)}
                disabled={busy}
                onValueChange={(value) => setType(Number(value))}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(REWARD_TYPE).map((value) => (
                    <SelectItem key={value} value={String(value)}>
                      {t(`loyalty.type${value}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            {percent ? (
              <Field
                label={t('loyalty.percentOff')}
                htmlFor="value"
                hint={t('loyalty.percentHint')}
              >
                <Input
                  id="value"
                  type="number"
                  min={0}
                  max={100}
                  step="0.1"
                  value={percentText}
                  disabled={busy}
                  onChange={(event) => {
                    const text = event.target.value;
                    setPercentText(text);
                    const parsed = Number(text);
                    set(
                      'value',
                      text === '' || Number.isNaN(parsed) ? 0 : fromPercent(parsed)
                    );
                  }}
                />
              </Field>
            ) : (
              <Field label={t('loyalty.amountOff')} htmlFor="value">
                <Input
                  id="value"
                  type="number"
                  min={0}
                  step="0.01"
                  value={draft.value}
                  disabled={busy}
                  onChange={(event) => set('value', Number(event.target.value))}
                />
              </Field>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label={t('loyalty.maxValue')}
                htmlFor="maxValue"
                hint={t('loyalty.maxValueHint')}
              >
                <Input
                  id="maxValue"
                  type="number"
                  min={0}
                  step="0.01"
                  value={draft.maxValue}
                  disabled={busy}
                  onChange={(event) =>
                    set('maxValue', Number(event.target.value))
                  }
                />
              </Field>
              <Field
                label={t('loyalty.minOrder')}
                htmlFor="minimumOrderTotal"
                hint={t('loyalty.minOrderHint')}
              >
                <Input
                  id="minimumOrderTotal"
                  type="number"
                  min={0}
                  step="0.01"
                  value={draft.minimumOrderTotal}
                  disabled={busy}
                  onChange={(event) =>
                    set('minimumOrderTotal', Number(event.target.value))
                  }
                />
              </Field>
            </div>

            {draft.maxValue === 0 && (
              <p className="text-destructive text-xs">
                {t('loyalty.maxValueZero')}
              </p>
            )}

            {/* ------------------------------------------------- product */}
            {needsItem(draft.type) && (
              <Field label={t('loyalty.product')} hint={t('loyalty.productHint')}>
                {item ? (
                  <div className="flex items-center gap-2.5 rounded-xl border p-2.5">
                    {item.itemThumbnail || item.itemImage ? (
                      <img
                        src={item.itemThumbnail ?? item.itemImage ?? ''}
                        alt=""
                        className="bg-muted size-9 shrink-0 rounded-md object-contain"
                      />
                    ) : (
                      <div className="bg-muted text-muted-foreground flex size-9 shrink-0 items-center justify-center rounded-md">
                        <ImageOff className="size-3.5" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm">{item.name || '—'}</p>
                      <p className="text-muted-foreground truncate text-xs">
                        {[item.brand, item.gtin].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={t('loyalty.changeProduct')}
                      disabled={busy}
                      onClick={() => setItem(null)}
                    >
                      <X className="size-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <form
                      role="search"
                      className="flex gap-2"
                      onSubmit={(event) => {
                        event.preventDefault();
                        if (itemQuery.trim())
                          catalogSearch.mutate(itemQuery.trim());
                      }}
                    >
                      <div className="relative flex-1">
                        <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
                        <Input
                          value={itemQuery}
                          onChange={(event) => setItemQuery(event.target.value)}
                          placeholder={t('loyalty.searchItems')}
                          aria-label={t('loyalty.searchItems')}
                          className="pl-9"
                        />
                      </div>
                      <Button type="submit" disabled={catalogSearch.isPending}>
                        {catalogSearch.isPending ? (
                          <Loader2 className="animate-spin" />
                        ) : (
                          t('common.search')
                        )}
                      </Button>
                    </form>

                    {catalogSearch.isPending && (
                      <div className="divide-y rounded-xl border">
                        {Array.from({ length: 3 }).map((_, index) => (
                          <div key={index} className="space-y-1.5 p-2.5">
                            <Skeleton className="h-4 w-40" />
                            <Skeleton className="h-3 w-24" />
                          </div>
                        ))}
                      </div>
                    )}

                    {catalogSearch.isSuccess &&
                      catalogSearch.data.items.length === 0 && (
                        <p className="text-muted-foreground text-xs">
                          {t('loyalty.noItemsFound')}
                        </p>
                      )}

                    {catalogSearch.data &&
                      catalogSearch.data.items.length > 0 && (
                        <div className="max-h-48 divide-y overflow-y-auto rounded-xl border">
                          {catalogSearch.data.items.map((found) => (
                            <button
                              key={found.id}
                              type="button"
                              className="hover:bg-accent flex w-full items-center gap-2.5 p-2.5 text-left text-sm transition-colors"
                              onClick={() =>
                                setItem({
                                  id: found.id,
                                  gtin: null,
                                  brand: found.brand,
                                  name: found.name,
                                  itemImage: found.itemImage,
                                  itemThumbnail: found.itemImage,
                                })
                              }
                            >
                              <div className="min-w-0 flex-1">
                                <p className="truncate">{found.name || '—'}</p>
                                <p className="text-muted-foreground truncate text-xs">
                                  {found.brand}
                                </p>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                  </div>
                )}
              </Field>
            )}
          </Section>

          {/* ---------------------------------------------------- window */}
          <Section title={t('loyalty.schedule')}>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('loyalty.starts')} htmlFor="start">
                <Input
                  id="start"
                  type="date"
                  value={start}
                  disabled={busy}
                  onChange={(event) => setStart(event.target.value)}
                />
              </Field>
              <Field label={t('loyalty.ends')} htmlFor="end">
                <Input
                  id="end"
                  type="date"
                  value={end}
                  disabled={busy}
                  onChange={(event) => setEnd(event.target.value)}
                />
              </Field>
            </div>
            {startsAfterEnds && (
              <p className="text-destructive text-xs">
                {t('loyalty.endBeforeStart')}
              </p>
            )}
          </Section>

          <div className="flex justify-end gap-2 pt-1">
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => onOpenChange(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button disabled={!canSave || busy} onClick={submit}>
              {save.isPending && <Loader2 className="animate-spin" />}
              {isEdit ? t('common.save') : t('loyalty.create')}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
